import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommunicationPreferencesUpdateInput,
  ConsentInitializationInput,
  EmailChangeConfirmationInput,
  EmailChangeRequestInput,
  ProfileUpdateInput,
} from '@emazy/shared-types';

import { useAuth } from '@/features/auth/context';

import { CustomerProfileApiError, customerProfileKeys } from '../api';
import { useCustomerProfileApi } from '../context/customerProfileApiContext';

export function useCustomerProfile() {
  const api = useCustomerProfileApi();
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: customerProfileKeys.profile(),
    queryFn: () => api.getCustomerProfile(),
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: (count, error) =>
      !(error instanceof CustomerProfileApiError && error.status < 500) && count < 2,
  });
}

function useProfileMutation<TInput>(mutationFn: (input: TInput) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: (value) => {
      if (value && typeof value === 'object' && 'version' in value) {
        queryClient.setQueryData(customerProfileKeys.profile(), (current: unknown) => {
          if (!current || typeof current !== 'object') return current;
          return { ...current, profile: value };
        });
      }
    },
    onError: (error) => {
      if (error instanceof CustomerProfileApiError && error.code === 'profile-conflict')
        void queryClient.invalidateQueries({ queryKey: customerProfileKeys.profile() });
    },
  });
}

export function useUpdateCustomerProfile() {
  const api = useCustomerProfileApi();
  return useProfileMutation<ProfileUpdateInput>((input) => api.updateCustomerProfile(input));
}

export function useUpdateCommunicationPreferences() {
  const api = useCustomerProfileApi();
  return useProfileMutation<CommunicationPreferencesUpdateInput>((input) =>
    api.updateCommunicationPreferences(input),
  );
}

export function useInitializeConsents() {
  const api = useCustomerProfileApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConsentInitializationInput) => api.initializeConsents(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customerProfileKeys.profile() }),
        queryClient.invalidateQueries({ queryKey: customerProfileKeys.consents() }),
      ]);
    },
  });
}

export function useConsentHistory(limit = 20) {
  const api = useCustomerProfileApi();
  const { isAuthenticated } = useAuth();
  return useInfiniteQuery({
    queryKey: customerProfileKeys.consentHistory({ limit }),
    queryFn: ({ pageParam }) =>
      api.getConsentHistory({ limit, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

export function useStartEmailChange() {
  const api = useCustomerProfileApi();
  return useMutation({
    mutationFn: (input: EmailChangeRequestInput) => api.startEmailChange(input),
    retry: false,
  });
}

export function useConfirmEmailChange() {
  const api = useCustomerProfileApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailChangeConfirmationInput) => api.confirmEmailChange(input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customerProfileKeys.profile() });
    },
  });
}
